// Timeline view (PLAN §9, §12.7 screen 7; task 29): events and relation periods for one or more Actors, one
// swimlane per Actor. The API assembles the items through the claims service; everything here is pure and shared
// (the item shapes, date spans at their own precision, axis ticks, row packing, filters).

import type { EntitySummary, ReviewStatus } from './claims';
import type { DatePrecision, FuzzyDate } from './fuzzy-date';
import type { ReliabilityStatus } from './reliability';
import type { RelationCategory } from './schema/types';
import type { Sensitivity } from './uploads';

/** Lanes on one timeline. */
export const TIMELINE_MAX_LANES = 12;
/** Links read per request (all lanes together); more are reported as `truncated`. */
export const TIMELINE_MAX_LINKS = 1500;

export const TIMELINE_ITEM_KINDS = ['event', 'period', 'entry'] as const;
/**
 * `event`: an Event the lane's Actor took part in (a diamond at its date, or a bar for a multi-day event).
 * `period`: a relation with dates ("employed by Meridian 2015–2018"; a single date is a point).
 * `entry`: a dated table row that points at no Event ("Controversies: Leaked memo, 2021").
 */
export type TimelineItemKind = (typeof TIMELINE_ITEM_KINDS)[number];

export const TIMELINE_KIND_LABELS: Record<TimelineItemKind, string> = {
  event: 'Events',
  period: 'Relation periods',
  entry: 'Other dated entries',
};

export interface TimelineItem {
  /** Unique on the timeline: `<lane id>:<event id or claim id>`. */
  id: string;
  /** The lane (Actor) this item belongs to. */
  lane: string;
  kind: TimelineItemKind;
  /** The Event, or the entity at the other end of the relation; null for an entry. */
  entityId: string | null;
  /** The claims that put the item on this lane (an Event reached twice, e.g. "speaker at" and a Key events row). */
  claimIds: string[];
  /** What the lane's Actor is to it, from the Actor's side: "speaker at", "employed by", "Key events". */
  roles: string[];
  /** Entry text (a row with no entity): its first text value. */
  text: string | null;
  category: RelationCategory | 'row' | null;
  /** ISO prefixes (YYYY, YYYY-MM, YYYY-MM-DD); a coarser date covers its whole span. Both null = undated. */
  from: string | null;
  to: string | null;
  /** Ongoing ("since 2021"): runs to today. */
  openEnd: boolean;
  approximate: boolean;
  precision: DatePrecision | null;
  /** The date as written ("c. 2021", "2015–2018"). */
  dateText: string | null;
  /** Where the date came from: the Event's own date, the table row, or the relation's period. */
  dateSource: 'event' | 'row' | 'claim' | null;
  /** The least settled status among the claims (disputed › unreviewed › verified). */
  status: Exclude<ReviewStatus, 'rejected'>;
  /** The most restrictive sensitivity among the claims and the Event. */
  sensitivity: Sensitivity;
  /** The strongest reliability status among the claims (Official document › Corroborated › Single source › Hypothesis). */
  reliability: ReliabilityStatus | null;
}

export interface TimelineData {
  /** The lanes, in the order asked for (entities that no longer exist are left out). */
  lanes: EntitySummary[];
  /** Events and linked entities the items point at (with rules-mode styles); an item pointing at a lane uses `lanes`. */
  entities: EntitySummary[];
  items: TimelineItem[];
  /** More links matched than were read. */
  truncated: boolean;
}

const ID = /^[0-9A-Za-z]{1,40}$/;

/** Lane ids from a comma list (URL `?ids=`), deduplicated, malformed ones dropped, at most `TIMELINE_MAX_LANES`. */
export function parseTimelineIds(v: unknown): string[] {
  const raw = Array.isArray(v) ? v.join(',') : typeof v === 'string' ? v : '';
  return [
    ...new Set(
      raw
        .split(',')
        .map(s => s.trim())
        .filter(s => ID.test(s)),
    ),
  ].slice(0, TIMELINE_MAX_LANES);
}

/** A fuzzy date as item fields: quarters and seasons use their covered span, single dates are a point. */
export function fuzzyToItemDate(
  d: FuzzyDate | null | undefined,
): Pick<TimelineItem, 'from' | 'to' | 'openEnd' | 'approximate' | 'precision' | 'dateText'> | null {
  if (!d || typeof d !== 'object' || (!d.from && !d.to)) return null;
  const from = d.from ? (d.from.span?.from ?? d.from.value) : null;
  const to = d.to ? (d.to.span?.to ?? d.to.value) : d.openEnd ? null : from;
  return {
    from,
    to,
    openEnd: !!d.openEnd,
    approximate: !!(d.from?.approximate || d.to?.approximate),
    precision: d.from?.precision ?? d.to?.precision ?? null,
    dateText: d.label || d.raw || null,
  };
}

const ISO = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;

/** First instant covered by an ISO prefix ("2020" → 1 Jan 2020), in ms; NaN when malformed. */
export function isoStart(prefix: string): number {
  const m = ISO.exec(prefix);
  if (!m) return Number.NaN;
  return Date.UTC(Number(m[1]), m[2] ? Number(m[2]) - 1 : 0, m[3] ? Number(m[3]) : 1);
}

/** Last instant covered by an ISO prefix ("2020" → end of 31 Dec 2020), in ms; NaN when malformed. */
export function isoEnd(prefix: string): number {
  const m = ISO.exec(prefix);
  if (!m) return Number.NaN;
  const y = Number(m[1]);
  if (!m[2]) return Date.UTC(y + 1, 0, 1) - 1;
  const mo = Number(m[2]);
  if (!m[3]) return Date.UTC(y, mo, 1) - 1;
  return Date.UTC(y, mo - 1, Number(m[3]) + 1) - 1;
}

/**
 * The span an item covers, in ms: from the start of `from` to the end of `to`, each at its own precision ("2015–2018"
 * runs to the end of 2018). Ongoing items run to `now`. Null for undated items.
 */
export function itemSpan(item: Pick<TimelineItem, 'from' | 'to' | 'openEnd'>, now = Date.now()): { start: number; end: number } | null {
  const a = item.from ?? item.to;
  if (!a) return null;
  const start = isoStart(a);
  const end = item.openEnd ? Math.max(now, isoEnd(a)) : isoEnd(item.to ?? a);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end: Math.max(start, end) };
}

export const isDated = (item: Pick<TimelineItem, 'from' | 'to'>) => !!(item.from || item.to);

/** Filters the timeline understands (client side). Everything optional = show all. */
export interface TimelineFilter {
  kinds?: TimelineItemKind[];
  statuses?: Exclude<ReviewStatus, 'rejected'>[];
  /** Only items overlapping this window (years, inclusive). */
  fromYear?: number | null;
  toYear?: number | null;
}

export function itemMatches(item: TimelineItem, f: TimelineFilter, now = Date.now()): boolean {
  if (f.kinds?.length && !f.kinds.includes(item.kind)) return false;
  if (f.statuses?.length && !f.statuses.includes(item.status)) return false;
  if (f.fromYear || f.toYear) {
    const s = itemSpan(item, now);
    if (!s) return false;
    if (f.fromYear && s.end < Date.UTC(f.fromYear, 0, 1)) return false;
    if (f.toYear && s.start > Date.UTC(f.toYear + 1, 0, 1) - 1) return false;
  }
  return true;
}

/** The window the axis shows: the items' extent (or the filter's years), padded a little on both sides. */
export function timelineWindow(
  items: Pick<TimelineItem, 'from' | 'to' | 'openEnd'>[],
  f: Pick<TimelineFilter, 'fromYear' | 'toYear'> = {},
  now = Date.now(),
): { start: number; end: number } | null {
  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;
  for (const i of items) {
    const s = itemSpan(i, now);
    if (!s) continue;
    start = Math.min(start, s.start);
    end = Math.max(end, s.end);
  }
  if (f.fromYear) start = Date.UTC(f.fromYear, 0, 1);
  if (f.toYear) end = Date.UTC(f.toYear + 1, 0, 1) - 1;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  if (end < start) [start, end] = [end, start];
  const DAY = 86_400_000;
  // At least a month wide, so a single dated event sits in the middle of a readable axis.
  const width = Math.max(end - start, 30 * DAY);
  const pad = width * 0.04;
  const mid = (start + end) / 2;
  return f.fromYear || f.toYear
    ? { start, end: Math.max(end, start + DAY) }
    : { start: Math.min(start, mid - width / 2) - pad, end: Math.max(end, mid + width / 2) + pad };
}

export interface TimelineTick {
  at: number;
  label: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Axis ticks for a window: whole decades, years, quarters or months, whichever gives at most `max` ticks. Labels are
 * plain numbers and English month names (the interface is English only; data can be in any language).
 */
export function timelineTicks(window: { start: number; end: number }, max = 10): TimelineTick[] {
  const s = new Date(window.start);
  const e = new Date(window.end);
  const years = e.getUTCFullYear() - s.getUTCFullYear();
  const out: TimelineTick[] = [];
  const steps = [100, 50, 20, 10, 5, 2, 1];
  const yearStep = [...steps].reverse().find(st => Math.ceil((years + 1) / st) <= max);
  if (years >= 2 && yearStep) {
    const first = Math.ceil(s.getUTCFullYear() / yearStep) * yearStep;
    for (let y = first; y <= e.getUTCFullYear(); y += yearStep) {
      const at = Date.UTC(y, 0, 1);
      if (at >= window.start && at <= window.end) out.push({ at, label: String(y) });
    }
    if (out.length) return out;
  }
  const months = years * 12 + e.getUTCMonth() - s.getUTCMonth();
  const monthStep = [1, 2, 3, 6, 12].find(st => Math.ceil((months + 1) / st) <= max) ?? 12;
  let y = s.getUTCFullYear();
  let m = Math.ceil(s.getUTCMonth() / monthStep) * monthStep;
  for (let guard = 0; guard < 400; guard++) {
    if (m >= 12) {
      y += Math.floor(m / 12);
      m %= 12;
    }
    const at = Date.UTC(y, m, 1);
    if (at > window.end) break;
    if (at >= window.start) out.push({ at, label: m === 0 ? String(y) : `${MONTHS[m]} ${y}` });
    m += monthStep;
  }
  return out;
}

/**
 * Packs spans into rows so none overlap (greedy, by start): returns the row of each span, in input order. `gap` is
 * the minimum distance kept between neighbours (room for a label), in the spans' own unit.
 */
export function packRows(spans: { start: number; end: number }[], gap = 0): number[] {
  const order = spans.map((s, i) => ({ ...s, i })).sort((a, b) => a.start - b.start || a.end - b.end);
  const rowEnds: number[] = [];
  const out = new Array<number>(spans.length).fill(0);
  for (const s of order) {
    let row = rowEnds.findIndex(end => end + gap <= s.start);
    if (row < 0) {
      row = rowEnds.length;
      rowEnds.push(s.end);
    } else rowEnds[row] = s.end;
    out[s.i] = row;
  }
  return out;
}

const STATUS_RANK: Record<Exclude<ReviewStatus, 'rejected'>, number> = { verified: 0, unreviewed: 1, disputed: 2 };

/** The least settled of two statuses (an Event reached by a verified and an unreviewed claim shows as unreviewed). */
export const worseStatus = (a: Exclude<ReviewStatus, 'rejected'>, b: Exclude<ReviewStatus, 'rejected'>) =>
  STATUS_RANK[b] > STATUS_RANK[a] ? b : a;

/** A readable date for an item: the date as written, else the ISO range. */
export function itemDateLabel(item: Pick<TimelineItem, 'from' | 'to' | 'openEnd' | 'dateText'>): string {
  if (item.dateText) return item.dateText;
  if (!item.from && !item.to) return 'Undated';
  if (item.openEnd) return `since ${item.from ?? item.to}`;
  if (!item.from) return `until ${item.to}`;
  return item.to && item.to !== item.from ? `${item.from} – ${item.to}` : item.from;
}
